import React from 'react';
import { connect } from 'react-redux';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { ActivitiesFooter } from 'component/common/Footer/ActivitiesFooter';
import CitySearchContent from 'component/page/search/City/CitySearchContent/CitySearchContent';
import { TopCitiesCarousel } from 'component/common/TopCitiesCarousel/TopCitiesCarousel';
import WhyChooseAgoda from 'component/common/WhyChooseAgoda/WhyChooseAgoda';
import PageContainer from 'component/common/PageContainer/PageContainer';
import CityHeader from 'component/page/search/City/CityHeader/CityHeader';
import NoResultsErrorPage from 'component/page/search/Error/NoResultsErrorPage';
import QuickCategoryFilter from 'component/page/search/City/QuickCategoryFilter/QuickCategoryFilter';
import { trackPageVisitEvent } from 'core/messaging/analytics/analytics';
import { CityPageContextFields } from 'core/messaging/analytics/pages/pageEventsMapping';
import { StickyTopBar } from 'component/common/StickyTopBar/StickyTopBar';
import SearchBar from 'component/common/SearchBar/SearchBar';
import { CityPageProps, mapStateToProps } from 'component/page/search/City/CityPage.selectors';
import { OrchestrateTopActivitiesInSessionStorage } from 'core-framework/util/EmptyStateSuggestion';
import ActivitiesPOIContainer from 'component/page/search/City/ActivitiesPOIContainer/ActivitiesPOIContainer';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { Box, Typography } from '@drone-js/core';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import TopSubcategoryContainer from 'component/page/search/City/TopSubcategory/TopSubcategoryContainer';
import { StringFormatter } from '@agoda/react-handyman';
import { CartFloatingContainer } from 'component/common/CartFloatingButtonContainer/CartFloatingButtonContainer';

const CityPageComponent: React.FC<CityPageProps> = (props: CityPageProps) => {
    const { cms, behavior, geo } = props.config;
    const isShowWhyChooseUs = behavior.home.showWhyChooseUs;
    const cityName = geo?.cityNameTranslated || geo?.cityName || '';

    React.useEffect(() => {
        if (props.isSearchBoxFeatureGroup) {
            OrchestrateTopActivitiesInSessionStorage(props.config);
        }
    }, [props.config, props.isSearchBoxFeatureGroup]);

    React.useEffect(() => {
        trackPageVisitEvent<CityPageContextFields>({});
    }, []);

    if (!geo || geo.cityId <= 0 || cityName.length <= 0) {
        return <NoResultsErrorPage cityName={cityName || cms[44215]} isEmptyFromFiltered={false} selectedFilters={{ valueFilterMatrix: [], rangeFilterMatrix: [] }} />;
    }

    return (
        <PageContainer data-testid="activities-city-page" overflowY="visible">
            <StickyTopBar
                elementName="activities-header-search-bar"
                zIndex={props.isFixOverlayIssue ? 4 : undefined}
                contentAreaProps={{ px: '8', py: '8', display: 'flex', alignItems: 'center' }}
                noShadow={!props.isRemoveCityPageImage}
            >
                <SearchBar popularActivities={props.popularActivities} isDisabledGlobalKeywordSearch={true} />
            </StickyTopBar>
            <CityHeader />
            <Feature.On name={FeatureName.SubcategoryFilter}>
                <TopSubcategoryContainer topSubcategories={props.topSubcategories} activitiesCountText={cms[351143]} />
            </Feature.On>
            <Feature.Off name={FeatureName.PageContainerSpacing}>
                <ActivitiesPOIContainer />
            </Feature.Off>
            <Feature.On name={FeatureName.PageContainerSpacing}>
                <Box backgroundColor="base.surface">
                    <ContentContainer display="flex" flexDirection="column" mx="auto" my="auto" enforceDefaultSpacing={true} maxWidth="100%">
                        <ActivitiesPOIContainer />
                    </ContentContainer>
                </Box>
            </Feature.On>
            <Feature.Off name={FeatureName.SubcategoryFilter}>
                <QuickCategoryFilter />
            </Feature.Off>
            <Feature.On name={FeatureName.SubcategoryFilter}>
                {!props.hasNoTopSubCategories && (
                    <ContentContainer display="flex" flexDirection="column" mx="auto" my="auto" enforceDefaultSpacing={true} maxWidth="100%">
                        <Typography variant="heading-l" px="8">
                            {StringFormatter.formatToJsx(cms[298605], cityName)}
                        </Typography>
                    </ContentContainer>
                )}
                {props.hasNoTopSubCategories && <QuickCategoryFilter />}
            </Feature.On>
            <CitySearchContent />
            <TopCitiesCarousel />
            {isShowWhyChooseUs && <WhyChooseAgoda />}
            {props.isNotMobile && <CartFloatingContainer />}
            <ActivitiesFooter />
        </PageContainer>
    );
};

CityPageComponent.displayName = 'CityPage';
const CityPageWithError = withErrorBoundary(CityPageComponent);

export default connect(mapStateToProps)(CityPageWithError);
